import { Box, Typography } from '@mui/material';
import {
  Area,
  CartesianGrid,
  ComposedChart,
  Line,
  ResponsiveContainer,
  Tooltip,
  TooltipProps,
  XAxis,
  YAxis,
} from 'recharts';
import type { NameType, ValueType } from 'recharts/types/component/DefaultTooltipContent';

import HourglassIcon from '@/assets/svg/ui-elements/hourglass-coming-soon.svg';

import { formatAxisDate } from './bar-chart';
import { CardProps } from './common';
import { ChartCardContainer, ComingSoonBlur } from './styled';

type ChartCardProps = {
  card: CardProps;
  small?: boolean;
  withGradient?: boolean;
  withTooltip?: boolean;
  withYAxis?: boolean;
};

const formatMoney = (n: number) =>
  n >= 1_000_000
    ? `$${(n / 1_000_000).toFixed(1)}M`
    : n >= 1_000
      ? `$${(n / 1_000).toFixed(0)}K`
      : `$${n.toFixed(2)}`;

const CustomTooltip = ({
  active,
  payload,
  label,
  isDollar,
}: {
  active?: boolean;
  payload?: TooltipProps<ValueType, NameType>['payload'];
  label?: string;
  isDollar: boolean;
}) => {
  if (!active || !payload?.length) return null;
  const val = Number(payload[0].value);
  return (
    <Box
      sx={{
        padding: '4px',
        bgcolor: 'rgba(20,23,40,0.9)',
        borderRadius: '6px',
      }}
    >
      <Typography color="#39CA88" fontSize={10} fontWeight={700}>
        {isDollar ? formatMoney(val) : val.toLocaleString()}
      </Typography>
      <Typography fontWeight="medium" fontSize={10}>
        {label}
      </Typography>
    </Box>
  );
};

export const ChartCard = ({
  card,
  small = false,
  withGradient = false,
  withTooltip = false,
  withYAxis = false,
}: ChartCardProps) => {
  const getYAxisTickFormatter = (isDollar: boolean) => (val: number) =>
    isDollar ? formatMoney(val) : val.toLocaleString();

  return (
    <ChartCardContainer small={small}>
      <Typography variant="body2" color="#9CA0A2" mb={small ? '12px' : '16px'}>
        {card.title}
      </Typography>
      <Typography fontSize={30} fontWeight={700}>
        {card.value}{' '}
        <Typography
          component="span"
          variant="body2"
          fontWeight={withGradient ? 700 : 500}
          color={withGradient ? '#FFF' : '#39CA88'}
        >
          {card.delta}
        </Typography>
      </Typography>

      <Box height={small ? '170px' : '200px'} mt={small ? '12px' : '37px'}>
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart
            data={card?.chartData}
            margin={{ top: 0, right: 0, bottom: 0, left: withYAxis ? -10 : -30 }}
          >
            {withGradient && (
              <defs>
                <linearGradient id="lineGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#10E182" stopOpacity={0.1} />
                  <stop offset="100%" stopColor="#10E182" stopOpacity={0.03} />
                </linearGradient>
              </defs>
            )}

            <CartesianGrid
              strokeDasharray={withGradient ? '' : '6 6'}
              stroke="#525768"
              vertical={withGradient}
              horizontal={!withGradient}
            />

            <XAxis
              dataKey="date"
              tickFormatter={formatAxisDate}
              tick={{ fill: '#9CA0A2', fontSize: 10 }}
              axisLine={false}
              tickLine={false}
              minTickGap={20}
            />

            {withYAxis ? (
              <YAxis
                tick={{ fill: '#9CA0A2', fontSize: 10 }}
                tickFormatter={getYAxisTickFormatter(card.dollarAmount)}
                domain={['auto', 'auto']}
                axisLine={false}
                tickLine={false}
              />
            ) : (
              <YAxis hide domain={['dataMin - 10', 'dataMax + 10']} />
            )}

            {withTooltip && (
              <Tooltip
                cursor={{ stroke: '#272C42', strokeWidth: 1 }}
                content={<CustomTooltip isDollar={card.dollarAmount} />}
                wrapperStyle={{ outline: 'none' }}
              />
            )}

            {withGradient && (
              <Area
                type="monotone"
                dataKey="count"
                stroke="none"
                fill="url(#lineGradient)"
                isAnimationActive={false}
              />
            )}

            <Line
              type="monotone"
              dataKey="count"
              stroke={card.strokeColor ?? '#10E182'}
              strokeWidth={2}
              dot={false}
            />
          </ComposedChart>
        </ResponsiveContainer>
      </Box>

      {card.isComingSoon && (
        <ComingSoonBlur>
          <HourglassIcon />
          <Typography mt="8px" fontWeight="medium">
            Coming Soon
          </Typography>
        </ComingSoonBlur>
      )}
    </ChartCardContainer>
  );
};
